"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.sumBy = exports.groupBy = exports.sortBy = exports.compare = exports.isNumber = exports.getCouponPromotionValue = exports.getPercentPromotionValue = exports.getPromotionalPrice = exports.computePromotionalPriceForQuantity = exports.computePromotionalPrice = exports.round = exports.sumOfRounded = void 0;
/** Σ(round(i)) */
const sumOfRounded = (nums) => nums.reduce((acc, cur) => acc + Math.round(cur), 0);
exports.sumOfRounded = sumOfRounded;
/**
 * Rounds a given number to a specified precision.
 */
const round = (value, precision = 0) => {
    const scale = 10 ** precision;
    return Math.round(value * scale) / scale;
};
exports.round = round;
/**
 * Computes a simple unitary discount
 *
 * @param originalPrice - the price before promotion
 * @param value - the value of the discount (in amount or in percent according to the type)
 * @param type - the type of the promotion, percent is the discount percentage to apply
 *
 * @return the promotional price computed
 *
 * @public
 */
const computePromotionalPrice = (originalPrice, { value, type, }) => {
    let promotionalPrice = originalPrice;
    switch (type) {
        case 'VALUE':
            promotionalPrice = (0, exports.round)(value, 2);
            break;
        case 'PERCENT':
            promotionalPrice = (0, exports.round)(originalPrice * (1 - value / 100), 2);
            break;
    }
    return promotionalPrice;
};
exports.computePromotionalPrice = computePromotionalPrice;
/**
 * Compute a promotional price for a given promo and a given quantity
 *
 * @example
 * * Given [originalPrice = 2€, nthQuantity = 3, promo = 100%, quantity = 4],
 * → [total = 6€, unitPrice = 1,50€]
 * * Given [originalPrice = 3€, nthQuantity = 2, promo = 1€, quantity = 4],
 * → [total = 8€, unitPrice = 2€]
 *
 * @param originalPrice - the price before promotion
 * @param promo - the promo to apply
 * @param quantity - the number of items
 *
 * @returns the total price for the given quantity and the new unitPrice after promotion (= total / quantity)
 *
 * @public
 */
const computePromotionalPriceForQuantity = (originalPrice, promo, quantity = 1) => {
    const promotionalPrice = promo
        ? (0, exports.computePromotionalPrice)(originalPrice, promo.conditions)
        : originalPrice;
    if (!quantity || !(promo === null || promo === void 0 ? void 0 : promo.conditions.nthQuantity)) {
        return {
            unitPrice: promotionalPrice,
            total: (0, exports.round)(promotionalPrice * quantity, 2),
        };
    }
    const nthQuantity = promo.conditions.nthQuantity;
    const nbApplicable = Math.trunc(quantity / nthQuantity);
    const total = (0, exports.round)(nbApplicable * promotionalPrice + (quantity - nbApplicable) * originalPrice, 2);
    const unitPrice = (0, exports.round)(total / quantity, 2);
    return { total, unitPrice };
};
exports.computePromotionalPriceForQuantity = computePromotionalPriceForQuantity;
/**
 * returns the price with the promo applied
 *
 * @remarks
 *
 * ⚠ If the promo applies to a specific quantity (see {@link AccountingPromo#conditions.nthQuantity}),
 * then the promotionalPrice is computed unitary for this given quantity
 *
 * @example Given [originalPrice = 3€, nthQuantity = 3, promo = 100%], the promotionalPrice will be 6€ / 3 = 2€
 *
 * @param originalPrice - the price before promotion
 * @param promo - the promo to apply
 *
 * @returns the new price after promotion
 *
 * @public
 */
const getPromotionalPrice = (originalPrice, promo) => {
    if (promo) {
        if (promo.conditions.nthQuantity) {
            return (0, exports.computePromotionalPriceForQuantity)(originalPrice, promo, promo.conditions.nthQuantity).unitPrice;
        }
        return (0, exports.computePromotionalPrice)(originalPrice, promo.conditions);
    }
    return (0, exports.round)(originalPrice, 2);
};
exports.getPromotionalPrice = getPromotionalPrice;
/**
 * returns the converted value in cents of the percent promotion
 *
 * @param originalPrice - the price before promotion
 * @param percentValue - the promotion value in percent
 * @param maxValue - maximum value of the promotion. If undefined : no max limit.
 *
 * @returns the converted and rounded value in cents
 *
 * @public
 */
const getPercentPromotionValue = (originalPrice, percentValue, maxValue) => {
    const promotion = (0, exports.round)(originalPrice -
        (0, exports.getPromotionalPrice)(originalPrice, {
            conditions: { type: 'PERCENT', value: percentValue },
        }));
    return maxValue ? Math.min(promotion, maxValue) : promotion;
};
exports.getPercentPromotionValue = getPercentPromotionValue;
/**
 * return the promotional value of a coupon for the given articles
 *
 * @param articles - the articles and their associated prices (total price for a given article, not the unit price)
 * @param coupon - the coupon to apply
 *
 * @returns the value of the coupon
 *
 * @public
 */
const getCouponPromotionValue = ({ articles, coupon, }) => {
    if (coupon.type !== 'VALUE' && coupon.type !== 'PERCENT') {
        return 0;
    }
    // do not apply this rule if no coupon is provided
    if (coupon.articleIdsToApplyOn && coupon.articleIdsToApplyOn.length > 0) {
        const toApplyIds = coupon.articleIdsToApplyOn;
        const articlesApplied = articles.filter(a => toApplyIds.some(id => id === a.articleId));
        if (coupon.type === 'VALUE') {
            return Math.min(coupon.value, (0, exports.sumOfRounded)(articlesApplied.map(a => a.net)));
        }
        // if coupon is a percent type
        return (0, exports.getPercentPromotionValue)((0, exports.sumOfRounded)(articlesApplied.map(a => a.net)), coupon.value, coupon.maxCouponValue);
    }
    return coupon.type === 'VALUE'
        ? coupon.value
        : (0, exports.getPercentPromotionValue)((0, exports.sumOfRounded)(articles.map(a => a.net)), coupon.value, coupon.maxCouponValue);
};
exports.getCouponPromotionValue = getCouponPromotionValue;
/**
 * A type guard function that checks if a given value is a finite number.
 *
 * This function determines if the input is of type 'number' and is a finite numeric value.
 * It can be used to ensure the safety of type narrowing in TypeScript when dealing with
 * unknown or mixed types.
 *
 * @param value - The value to be checked. Can be of any type.
 * @returns A boolean indicating whether the input is a finite number.
 */
const isNumber = (value) => typeof value === 'number' && Number.isFinite(value);
exports.isNumber = isNumber;
/**
 * Compares the values passed as arguments and returns:
 * - If `valA = valB`: `0`
 * - If `valA < valB`: `1`
 * - If `valA > valB`: `-1`
 *
 * The ordering depends on the type of the arguments:
 * - If both values are numbers, a numeric comparison is used.
 * - Otherwise, the values are compared alphabetically after being converted to strings.
 */
const compare = (valA, valB) => {
    if (String(valA) === String(valB)) {
        return 0;
    }
    if ((0, exports.isNumber)(valA) && (0, exports.isNumber)(valB)) {
        return valA > valB ? 1 : -1;
    }
    return valA.toLocaleString().localeCompare(valB.toLocaleString()) > 0
        ? 1
        : -1;
};
exports.compare = compare;
/**
 * Sorts a list of elements using the value defined by `getSortValue`.
 * It is possible to force reverse sorting by passing `true` as the 3rd argument.
 */
const sortBy = (items, getSortValue, reverse = false) => {
    const dir = reverse ? -1 : 1;
    return items
        .map(item => ({ item, val: getSortValue(item) }))
        .sort((a, b) => dir * (0, exports.compare)(a.val, b.val))
        .map(({ item }) => item);
};
exports.sortBy = sortBy;
/**
 * Groups an array of items into an object, where the keys are derived from a function
 * and the values are arrays of items corresponding to those keys.
 *
 * @template T - The type of items in the array.
 * @template K - The type of the keys returned by the `getItemKey` function. Must be `string` or `number`.
 * @param {T[]} items - The array of items to be grouped.
 * @param {(item: T, index: number) => K} getItemKey - A function that takes an item and its index as arguments
 * and returns a key used for grouping that item.
 * @returns {Record<K, T[]>} An object mapping each unique key produced by the `getItemKey` function
 * to an array of items that share that key.
 */
const groupBy = (items, getItemKey) => {
    const groups = {};
    items.forEach(item => {
        var _a;
        const key = getItemKey(item);
        ((_a = groups[key]) !== null && _a !== void 0 ? _a : (groups[key] = [])).push(item);
    });
    return groups;
};
exports.groupBy = groupBy;
/**
 * Computes the sum of numbers derived from the provided array of items.
 *
 * @template T The type of elements in the items array.
 * @param {T[]} items The array of items to iterate over.
 * @param {function(T): number} getItemValue A function that returns a numeric value for each item in the array.
 * @returns {number} The total sum of numeric values obtained by applying the getItemValue function to each item in the array.
 */
const sumBy = (items, getItemValue) => items.reduce((acc, cur) => acc + getItemValue(cur), 0);
exports.sumBy = sumBy;
